import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

const createHelpers = (width, height, css) => {
    // somehow sizes is ending up in markup, even if it is not a valid svg attribute
    // until we have a better solution, just render it empty, instead to '[Object object]'
    const sanitizeSizes = sizes =>
        Object.defineProperty(sizes, "toString", {
            value: () => "",
            enumerable: false
        });

    const getDimensions = (size, sizes) => {
        if (
            size &&
            typeof size.width === "number" &&
            typeof size.height === "number"
        ) {
            return size;
        }

        return size && sizes[size] ? sizes[size] : { width, height };
    };

    const getCss = (size, sizes, fillColor, fillColorRule, noStyles) => {
        if (noStyles) {
            return "";
        }

        const dimensions = getDimensions(size, sizes);
        const fillRule =
            fillColor && fillColorRule
                ? `${fillColorRule}{ fill: ${fillColor}; }`
                : "";

        return css`
            width: ${dimensions.width}px;
            height: ${dimensions.height}px;
            ${fillRule}
        `;
    };

    const propsToCss = ({ size, sizes, fillColor, fillColorRule, noStyles }) =>
        getCss(size, sizes, fillColor, fillColorRule, noStyles);

    return {
        getCss,
        getDimensions,
        propsToCss,
        sanitizeSizes
    };
};

const width = "41";
const height = "40";
const viewBox = "0 0 41 40";

const { getDimensions, getCss, propsToCss, sanitizeSizes } = createHelpers(
    width,
    height,
    css
);

const sizes = sanitizeSizes({
    xs: { width: 14, height: 14 },
    sm: { width: 16, height: 16 },
    md: { width: 20, height: 20 },
    lg: { width: 24, height: 24 },
    xl: { width: 40, height: 40 }
});

const Image = styled.svg`
    ${propsToCss}
`;

const children = (
    <Fragment>
        <path fill="url(#pattern4)" d="M0 0h41v40H0z" key="key-0" />
        <defs key="key-1">
            <pattern
                id="pattern4"
                width="1"
                height="1"
                patternContentUnits="objectBoundingBox"
            >
                <use transform="scale(.0091 .00926)" xlinkHref="#image4" />
            </pattern>

            <image
                id="image4"
                width="110"
                height="108"
                xlinkHref="data:image/png;base64,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"
            />
        </defs>
    </Fragment>
);

const defaultProps = {
    children,
    viewBox,
    fillColor: null,
    fillColorRule: "&&& path, &&& use, &&& g",
    sizes,
    size: null
};

Image.propTypes /* remove-proptypes */ = {
    fillColor: PropTypes.string,
    fillColorRule: PropTypes.string,
    viewBox: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired
        })
    ]),
    sizes: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
    })
};

export default Object.assign(Image, {
    getDimensions,
    getCss,
    defaultProps,
    displayName: "PowerPoint"
});
